/*
 * Cookie consent — native banner copy augmentation + consent-gated theme trackers.
 * spec: specs/cookie-consent-banner.md
 *
 * Pattern adapted from fringe-project (specs/tracking-consent-audit-remediation.md,
 * scripts/consent + klaviyo-features), ported to this standalone Pursuit theme:
 * no build step here, so this is a plain IIFE instead of ES modules.
 *
 * FR-03: augments Shopify's native cookie banner — links the inline "Privacy
 *        Policy" (and hides Shopify's orphaned trailing link), "User Agreement"
 *        and "Cookie Statement" using URLs from window.__cookieConsentCfg.
 * FR-04: gates theme-triggered trackers (Heatmap, px_DcDa9 pixel, Klaviyo onsite
 *        + legacy back-in-stock + add-to-cart) behind Customer Privacy consent.
 *        Fail-closed — nothing loads if the API is absent or consent is denied.
 */
(function () {
  'use strict';

  var cfg = window.__cookieConsentCfg || {};

  /* ------------------------------------------------------------------ *
   * Consent helper (purpose-based, fail-closed) — from fringe consent/  *
   * ------------------------------------------------------------------ */
  var PURPOSE_METHODS = {
    marketing: 'marketingAllowed',
    analytics: 'analyticsProcessingAllowed',
    preferences: 'preferencesProcessingAllowed',
    sale_of_data: 'saleOfDataAllowed'
  };

  var apiPromise = null;
  function loadConsentApi() {
    if (apiPromise) return apiPromise;
    apiPromise = new Promise(function (resolve, reject) {
      var shopify = window.Shopify;
      if (!shopify || typeof shopify.loadFeatures !== 'function') {
        reject(new Error('Customer Privacy API unavailable'));
        return;
      }
      shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function (error) { error ? reject(error) : resolve(window.Shopify.customerPrivacy); }
      );
    });
    return apiPromise;
  }

  // True when a consent banner is (or should be) shown to this visitor.
  //
  // Uses Shopify's own shouldShowBanner(), which already accounts for the shop's
  // region visibility settings, the visitor's location AND whether consent has
  // already been recorded. The docs are explicit about preferring it:
  //   "Use shouldShowBanner as the initial banner state [...] Use it instead of
  //    building custom logic to determine when to show a consent banner."
  //   https://shopify.dev/docs/api/customer-privacy
  //
  // The previous implementation OR'd shouldShowGDPRBanner() with
  // shouldShowCCPABanner(). That broke once the banner was scoped out of the US
  // (2026-08-11): shouldShowCCPABanner() stays true there because the data sale
  // opt-out page is still active, so the OR held ALL theme trackers waiting for
  // a decision from a banner that is never shown. Verified in production:
  // gdpr=false, ccpa=true, shouldShowBanner=false.
  function bannerShown(cp) {
    try {
      if (typeof cp.shouldShowBanner === 'function') return cp.shouldShowBanner() === true;
      // Fallback, only reached if the API predates shouldShowBanner(): hold under
      // the opt-in regime only (EEA/UK/CH). This mirrors the store's agreed
      // posture — opt-in in Europe, opt-out in the US — so it stays correct in
      // both regions instead of silently disabling trackers everywhere.
      return typeof cp.shouldShowGDPRBanner === 'function' && cp.shouldShowGDPRBanner() === true;
    } catch (e) { return false; }
  }

  // FR-04 AC-06: in opt-out regions (US/CCPA) Shopify reports purposes as
  // allowed while the visitor is still at 'no_interaction'. Where a banner is
  // shown, hold ALL theme tracking until the visitor makes an explicit choice.
  // Where NO banner is shown (unregulated), there is nothing to wait for, so we
  // honor Shopify's default and let trackers load (AC-04, no regression).
  function consentPending(cp) {
    try {
      return bannerShown(cp) &&
        typeof cp.getTrackingConsent === 'function' &&
        cp.getTrackingConsent() === 'no_interaction';
    } catch (e) { return true; }
  }

  function isAllowed(cp, purpose) {
    var method = PURPOSE_METHODS[purpose];
    if (!method || !cp || typeof cp[method] !== 'function') return false;
    try {
      if (consentPending(cp)) return false;
      return cp[method]() === true;
    } catch (e) { return false; }
  }

  // Runs `cb` once, as soon as consent for `purpose` is granted (immediately if
  // already granted, otherwise on the visitor's decision). Never runs if denied
  // or the API fails to load (fail-closed).
  function onConsentGranted(purpose, cb) {
    loadConsentApi().then(function (cp) {
      var done = false;
      function run() {
        if (done || !isAllowed(cp, purpose)) return;
        done = true;
        document.removeEventListener('visitorConsentCollected', run);
        cb();
      }
      run();
      if (!done) document.addEventListener('visitorConsentCollected', run);
    }).catch(function () { /* fail-closed: tracker never runs */ });
  }

  // Runs `cb(customerPrivacy)` on load and on every consent decision — for state
  // that must track grant AND revoke (e.g. the Klaviyo opt-out cookie).
  function onConsentChanged(cb) {
    loadConsentApi().then(function (cp) {
      cb(cp);
      document.addEventListener('visitorConsentCollected', function () { cb(cp); });
    }).catch(function () { /* fail-closed */ });
  }

  /* ------------------------------------------------------------------ *
   * FR-03 — native banner copy augmentation                             *
   * ------------------------------------------------------------------ */

  // Wraps the FIRST occurrence of `phrase` (a plain text node, never already
  // inside a link) in `root` with an <a href="href">. Idempotent per phrase via
  // the data-fr03-phrase marker, so re-runs never create a second link.
  function linkifyFirst(root, phrase, href) {
    if (!href) return;
    if (root.querySelector('a[data-fr03-phrase="' + phrase + '"]')) return;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var node;
    while ((node = walker.nextNode())) {
      if (node.parentElement.closest('a')) continue;
      var idx = node.nodeValue.indexOf(phrase);
      if (idx === -1) continue;
      var before = node.nodeValue.slice(0, idx);
      var after = node.nodeValue.slice(idx + phrase.length);
      var a = document.createElement('a');
      a.href = href;
      a.textContent = phrase;
      a.setAttribute('data-fr03-phrase', phrase);
      var frag = document.createDocumentFragment();
      if (before) frag.appendChild(document.createTextNode(before));
      frag.appendChild(a);
      if (after) frag.appendChild(document.createTextNode(after));
      node.parentNode.replaceChild(frag, node);
      return;
    }
  }

  function augmentBanner(banner) {
    try {
      var p = banner.querySelector('p');
      if (!p) return;
      // Privacy Policy: reuse the native link's href for the first inline
      // mention, then hide Shopify's trailing link so it isn't left orphaned.
      var nativeLink = banner.querySelector('#shopify-pc__banner__body-policy-link');
      var ppHref = nativeLink ? nativeLink.getAttribute('href') : null;
      if (ppHref) {
        linkifyFirst(p, 'Privacy Policy', ppHref);
        nativeLink.style.display = 'none';
      }
      // User Agreement + Cookie Statement come from theme settings (first mention).
      linkifyFirst(p, 'User Agreement', cfg.userAgreementUrl);
      linkifyFirst(p, 'Cookie Statement', cfg.cookieStatementUrl);
    } catch (e) { /* never break the page over banner styling */ }
  }

  function watchForBanner() {
    var existing = document.querySelector('#shopify-pc__banner');
    if (existing) { augmentBanner(existing); return; }
    var obs = new MutationObserver(function () {
      var banner = document.querySelector('#shopify-pc__banner');
      if (banner) { obs.disconnect(); augmentBanner(banner); }
    });
    obs.observe(document.documentElement, { childList: true, subtree: true });
    // Safety: stop observing after a while if the banner never appears.
    setTimeout(function () { obs.disconnect(); }, 20000);
  }

  /* ------------------------------------------------------------------ *
   * FR-04 — consent-gated theme trackers                                *
   * ------------------------------------------------------------------ */

  function injectScript(src, attrs) {
    var s = document.createElement('script');
    s.src = src;
    if (attrs) { Object.keys(attrs).forEach(function (k) { s.setAttribute(k, attrs[k]); }); }
    document.head.appendChild(s);
    return s;
  }

  // Heatmap.com (analytics) — verbatim from the former theme.liquid head snippet.
  function loadHeatmap() {
    if (window._heatmap_paq) return;
    (function (h, e, a, t, m, ap) {
      (h._heatmap_paq = []).push(['setTrackerUrl', (h.heatUrl = e) + a]);
      h.hErrorLogs = h.hErrorLogs || [];
      ap = t.createElement('script'); ap.src = h.heatUrl + 'preprocessor.min.js?sid=' + m; ap.defer = true;
      t.head.appendChild(ap);
      ['error', 'unhandledrejection'].forEach(function (ty) {
        h.addEventListener(ty, function (et) { h.hErrorLogs.push({ type: ty, event: et }); });
      });
    })(window, 'https://dashboard.heatmap.com/', 'heatmap.php', document, 5760);
  }

  // Unlabeled ad pixel px_DcDa9 (marketing) — was inline in theme.liquid head.
  function loadPxDcDa9() {
    if (document.querySelector('script[data-px-dcda9]')) return;
    injectScript('https://xp2023-pix.s3.amazonaws.com/px_DcDa9.js', { 'data-px-dcda9': 'true' });
  }

  // Klaviyo (marketing): onsite passive tracking + legacy back-in-stock init +
  // add-to-cart events. Loaded only after marketing consent is granted, so zero
  // *.klaviyo.com tracking requests fire before opt-in.
  var KLAVIYO_ID = 'QLQQgx';
  function loadKlaviyo() {
    if (window.__klaviyoConsentLoaded) return;
    window.__klaviyoConsentLoaded = true;

    // Passive onsite tracking loader (was theme.liquid:234).
    injectScript(
      'https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=' + KLAVIYO_ID,
      { async: 'true', 'data-klaviyo-onsite': 'true' }
    );

    // Legacy back-in-stock (was theme.liquid:320-355) — gated per decision.
    var legacy = injectScript('https://a.klaviyo.com/media/js/onsite/onsite.js');
    legacy.onload = function () {
      window.klaviyo = window.klaviyo || [];
      window.klaviyo.init({ account: KLAVIYO_ID, platform: 'shopify' });
      window.klaviyo.enable('backinstock', {
        trigger: {
          product_page_text: 'Notify Me When Available',
          product_page_class: 'btn',
          product_page_text_align: 'left',
          product_page_margin: '0px',
          replace_anchor: false
        },
        modal: {
          headline: '{product_name}',
          body_content: 'Please confirm the specific variant you want to be notified about. You will receive a notification when this item comes back in stock.',
          email_field_label: 'Email',
          button_label: 'Notify me when available',
          subscription_success_label: "You're in! We'll let you know when it's back.",
          footer_content: '',
          additional_styles: "@import url('https://fonts.googleapis.com/css?family=Helvetica+Neue');",
          drop_background_color: '#000',
          background_color: '#fff',
          text_color: '#222',
          button_text_color: '#fff',
          button_background_color: '#584f4f',
          close_button_color: '#ccc',
          error_background_color: '#fcd6d7',
          error_text_color: '#C72E2F',
          success_background_color: '#d3efcd',
          success_text_color: '#1B9500'
        }
      });
    };

    setupAddedToCartTracking();
  }

  // Klaviyo "Added to Cart" (was snippets/klaviyo-atc-snippet.liquid). Attached
  // only after marketing consent, so pre-consent add-to-carts are never tracked.
  function setupAddedToCartTracking() {
    if (window.__klaviyoAtcPatched) return;
    window.__klaviyoAtcPatched = true;
    var learnq = window._learnq = window._learnq || [];
    var origFetch = window.fetch;
    window.fetch = function () {
      var response = origFetch.apply(this, arguments);
      try {
        response.then(function (res) {
          if (res && res.url && res.url.indexOf('/cart/add') !== -1) {
            origFetch(window.location.origin + '/cart.js')
              .then(function (r) { return r.json(); })
              .then(function (cart) {
                learnq.push(['track', 'Added to Cart', {
                  total_price: cart.total_price / 100,
                  $value: cart.total_price / 100,
                  total_discount: cart.total_discount,
                  original_total_price: cart.original_total_price / 100,
                  items: cart.items
                }]);
              })
              .catch(function () { /* tracking only — never break the cart flow */ });
          }
        });
      } catch (e) { /* ignore */ }
      return response;
    };
  }

  // Klaviyo opt-out cookie sync (defense-in-depth): Klaviyo only auto-respects
  // Shopify consent for EU/EEA/UK/CH, so for the US posture the theme drives
  // Klaviyo's documented __kla_off opt-out cookie + a first-party marker the
  // head-start gate (in theme.liquid) reads synchronously.
  var KLAVIYO_OFF_COOKIE = '__kla_off';
  var MARKETING_MARKER = '_theme_marketing_consent';
  function syncKlaviyoConsentCookies(cp) {
    var granted = false;
    // Same FR-04 AC-06 hold as isAllowed(): keep the __kla_off opt-out (and no
    // granted marker) until the visitor explicitly consents where a banner shows.
    try { granted = !consentPending(cp) && cp.marketingAllowed() === true; } catch (e) { granted = false; }
    if (granted) {
      document.cookie = KLAVIYO_OFF_COOKIE + '=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      document.cookie = MARKETING_MARKER + '=granted; path=/; max-age=31536000';
    } else {
      document.cookie = KLAVIYO_OFF_COOKIE + '=true; path=/';
      document.cookie = MARKETING_MARKER + '=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    }
  }

  /* ------------------------------------------------------------------ *
   * Init                                                                *
   * ------------------------------------------------------------------ */

  // FR-03 runs regardless of consent (it only restyles the banner text).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchForBanner);
  } else {
    watchForBanner();
  }

  // FR-04 — load each tracker only once its purpose is granted.
  onConsentGranted('analytics', loadHeatmap);
  onConsentGranted('marketing', loadPxDcDa9);
  onConsentGranted('marketing', loadKlaviyo);

  // Keep Klaviyo's opt-out cookie in sync on every consent decision.
  onConsentChanged(syncKlaviyoConsentCookies);
})();
